import React, { useState, useEffect } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { getIndustry } from "../../../Apis/Organization-apis";
import {
  fetchGeneralCountries,
  fetchOwnershipTypeCategory,
} from "../../../Apis/OrganizationLocation";
import {
  MenuItem,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Box,
  Typography,
} from "@mui/material";
import useAuthStore from "../../../Zustand/Store/useAuthStore";

function OrganizationData({ data }) {
  const [industries, setIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [generalCategories, setGeneralCategories] = useState([]);
  const [formData, setFormData] = useState();
  const [applicationOptions, setApplicationOptions] = useState([]);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const { isLoggedIn, login, setAccess } = useAuthStore();

  // console.log("data is ",location.state.application_user_id);
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    {
      fetchOwnershipTypeCategory()
        .then((data) => {
          setGeneralCategories(data.businessownershiptypescategory);
        })
        .catch((err) => {
          setFormErrors(err.message);
        });
    }
  }, []);

  useEffect(() => {
    {
      getIndustry()
        .then((data) => {
          setIndustries(data.industries);
        })
        .catch((err) => {
          setFormErrors(err.message);
        });
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [generalCountries] = await Promise.all([fetchGeneralCountries()]);
        setCountries(generalCountries.countries || []);
      } catch (err) {
        toast.error("Failed to fetch countries or industries");
      } finally {
        setFetchLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const loadApplicationOptions = async () => {
      if (!formData) {
        return;
      }

      try {
        const params = new URLSearchParams();
        if (formData.client_id) {
          params.set("client_id", formData.client_id);
        }
        if (formData.signup_application_key) {
          params.set("signup_application_key", formData.signup_application_key);
        }

        const query = params.toString();
        const res = await axios.get(
          `${MAIN_URL}/api/organizations/signup/applications${
            query ? `?${query}` : ""
          }`,
          { headers: getApiHeaders() }
        );
        const apps = res?.data?.applications || [];
        setApplicationOptions(apps);

        setFormData((prev) => {
          if (
            !prev ||
            (Array.isArray(prev.application_ids) && prev.application_ids.length > 0)
          ) {
            return prev;
          }

          let defaultIds = [];
          if (prev.signup_application_key) {
            const key = String(prev.signup_application_key).toLowerCase();
            const match = apps.find(
              (app) =>
                String(app.application_name || "")
                  .toLowerCase()
                  .includes(key) ||
                (key === "pm" &&
                  /project\s*management|^pm$/i.test(
                    String(app.application_name || "")
                  ))
            );
            if (match) {
              defaultIds = [Number(match.application_id)];
            }
          }

          if (!defaultIds.length && apps.length === 1) {
            defaultIds = [Number(apps[0].application_id)];
          }

          return defaultIds.length
            ? { ...prev, application_ids: defaultIds }
            : prev;
        });
      } catch (error) {
        console.error("Error fetching signup applications:", error);
      }
    };

    loadApplicationOptions();
  }, [formData?.client_id, formData?.signup_application_key]);

  const handleApplicationToggle = (applicationId) => {
    const normalizedId = Number(applicationId);
    setFormData((prev) => {
      const currentIds = Array.isArray(prev?.application_ids)
        ? prev.application_ids.map(Number)
        : [];
      const nextIds = currentIds.includes(normalizedId)
        ? currentIds.filter((id) => id !== normalizedId)
        : [...currentIds, normalizedId];

      return {
        ...prev,
        application_ids: nextIds,
      };
    });
    setFormErrors((prev) => ({ ...prev, application_ids: "" }));
  };

  const isBusinessOrganizationType = (categoryId, categories = generalCategories) => {
    const selected = categories.find(
      (item) =>
        Number(item.general_business_ownership_type_category_id) ===
        Number(categoryId)
    );
    return (
      String(selected?.business_ownership_type_category_name || "")
        .trim()
        .toLowerCase() === "business"
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updatedData = {
        ...prev,
        [name]: value,
        ...(name === "general_country_id" && { general_state_id: "" }),
        ...(name === "general_state_id" && { general_city_id: "" }),
      };

      if (name === "general_business_ownership_type_category_id") {
        if (!isBusinessOrganizationType(value)) {
          updatedData.general_industry_id = "";
        }
      }
      return updatedData;
    });

    if (
      name === "general_business_ownership_type_category_id" &&
      !isBusinessOrganizationType(value)
    ) {
      setFormErrors((prev) => ({ ...prev, general_industry_id: "" }));
    }

    // Set states when country changes
    if (name === "general_country_id") {
      const selectedCountry = countries.find(
        (c) => c.general_country_id === parseInt(value)
      );
      setAvailableStates(selectedCountry?.states || []);
      setAvailableCities([]); // clear cities
    }

    // ✅ Get cities from selected STATE
    if (name === "general_state_id") {
      const selectedState = availableStates.find(
        (s) => s.general_state_id === parseInt(value)
      );
      setAvailableCities(selectedState?.cities || []);
    }

    // console.log("citirs", availableCities);

    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.organization_name)
      errors.organization_name = "Organization Name is required";
    if (!formData.organization_short_name)
      errors.organization_short_name = "Short Name is required";
    if (!formData.general_country_id)
      errors.general_country_id = "Country is required";
    if (!formData.general_business_ownership_type_category_id)
      errors.general_business_ownership_type_category_id = "State is required";
    if (
      !Array.isArray(formData.application_ids) ||
      formData.application_ids.length === 0
    ) {
      errors.application_ids = "Select at least one application";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  console.log("formdata", formData);

  const handleSubmit = async (e) => {
   
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      let res = await axios.post(
        `${MAIN_URL}/api/organizations`,
        {
          ...formData,
          general_industry_id: isBusinessOrganizationType(
            formData.general_business_ownership_type_category_id
          )
            ? formData.general_industry_id || null
            : null,
        },
        {
        headers: getApiHeaders(),
      });
      console.log("response", res);
      toast.success("Organization created successfully!");
      if (res?.data?.token) {
        const { token, user } = res.data;

        localStorage.setItem("token", token);
        setAccess(user?.role?.role?.system_role_name);
        login(user);
        navigate("/");
        
      }
    } catch (error) {
      console.log("error", error);
      if (error?.response?.data?.errors !== undefined) {
        setFormErrors(error.response.data.errors);
      } else {
        toast.error("Failed to create Organization");
      }
    } finally {
      setLoading(false);
    }
  };

//   if (fetchLoading) {
//     return (
//       <div className="mainSection d-flex align-items-center justify-content-center">
//         <CircularProgress />
//       </div>
//     );
//   }

  // function to select the ownership type
  const getSelectedOwnershipTypeName = () => {
    const selected = generalCategories.find(
      (item) =>
        item.general_business_ownership_type_category_id ===
        formData.general_business_ownership_type_category_id
    );
    return selected ? selected.business_ownership_type_category_name : "";
  };

  const compactFieldSx = {
    "& .MuiInputBase-root": { height: 36, fontSize: "0.85rem" },
    "& .MuiInputLabel-root": { fontSize: "0.85rem" },
    "& .MuiFormHelperText-root": { fontSize: "0.68rem", mt: 0, mb: 0 },
  };

  return (
    <form onSubmit={handleSubmit} className="organization-details-form">
      <div className="org-form-row">
        <TextField
          fullWidth
          size="small"
          label="Organization Name"
          name="organization_name"
          value={formData?.organization_name}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.organization_name}
          helperText={formErrors.organization_name}
          sx={compactFieldSx}
        />
      </div>
      <div className="org-form-row">
        <TextField
          fullWidth
          size="small"
          label="Organization Short Name"
          name="organization_short_name"
          value={formData?.organization_short_name}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.organization_short_name}
          helperText={formErrors.organization_short_name}
          sx={compactFieldSx}
        />
      </div>
      <div className="org-form-row">
        <TextField
          select
          fullWidth
          size="small"
          label="Organization Type"
          name="general_business_ownership_type_category_id"
          value={formData?.general_business_ownership_type_category_id}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.general_business_ownership_type_category_id}
          helperText={formErrors.general_business_ownership_type_category_id}
          sx={compactFieldSx}
        >
          {generalCategories.map((option) => (
            <MenuItem
              key={option?.general_business_ownership_type_category_id}
              value={option?.general_business_ownership_type_category_id}
            >
              {option.business_ownership_type_category_name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className="org-form-row">
        <TextField
          select
          fullWidth
          size="small"
          label="Industry"
          name="general_industry_id"
          value={
            isBusinessOrganizationType(
              formData?.general_business_ownership_type_category_id
            )
              ? formData?.general_industry_id
              : ""
          }
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.general_industry_id}
          helperText={formErrors.general_industry_id}
          disabled={
            !formData?.general_business_ownership_type_category_id ||
            !isBusinessOrganizationType(
              formData?.general_business_ownership_type_category_id
            )
          }
          sx={compactFieldSx}
        >
          {industries.map((option) => (
            <MenuItem
              key={option.general_industry_id}
              value={option?.general_industry_id}
            >
              {option.industry_name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className="org-form-row">
        <TextField
          select
          fullWidth
          size="small"
          label="Country"
          name="general_country_id"
          value={formData?.general_country_id}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.general_country_id}
          helperText={formErrors.general_country_id}
          sx={compactFieldSx}
        >
          {countries.map((option) => (
            <MenuItem
              key={option.general_country_id}
              value={option.general_country_id}
            >
              {option.country_name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className="org-form-row">
        <TextField
          select
          fullWidth
          size="small"
          label="State"
          name="general_state_id"
          value={formData?.general_state_id}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.general_state_id}
          helperText={formErrors.general_state_id}
          disabled={availableStates.length === 0}
          sx={compactFieldSx}
        >
          {availableStates.map((state) => (
            <MenuItem
              key={state.general_state_id}
              value={state.general_state_id}
            >
              {state.state_name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className="org-form-row">
        <TextField
          select
          fullWidth
          size="small"
          label="City"
          name="general_city_id"
          value={formData?.general_city_id}
          onChange={handleChange}
          margin="dense"
          error={!!formErrors.general_city_id}
          helperText={formErrors.general_city_id}
          disabled={availableCities.length === 0}
          sx={compactFieldSx}
        >
          {availableCities.map((city) => (
            <MenuItem key={city.general_city_id} value={city.general_city_id}>
              {city.city_name}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <Box className="org-form-applications">
        <Typography sx={{ fontSize: "0.8rem", mb: 0 }}>
          Applications for this organization
        </Typography>
        <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap", gap: 0, py: 0 }}>
          {applicationOptions.map((option) => (
            <FormControlLabel
              key={option.application_id}
              sx={{ mr: 1, my: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.25 }}
                  checked={(formData?.application_ids || [])
                    .map(Number)
                    .includes(Number(option.application_id))}
                  onChange={() => handleApplicationToggle(option.application_id)}
                  disabled
                />
              }
              label={
                option.application_name || `Application #${option.application_id}`
              }
            />
          ))}
        </FormGroup>
        {!!formErrors.application_ids && (
          <FormHelperText error sx={{ mt: 0 }}>
            {formErrors.application_ids}
          </FormHelperText>
        )}
      </Box>

      <input
        type="submit"
        className="btn btn-primary w-100"
        value={loading ? "Submitting..." : "Add Organization"}
        disabled={loading}
      />
    </form>
  );
}

export default OrganizationData;
